import React, { useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import classNames from "classnames";
import { Link } from "gatsby";
import PropTypes from "prop-types";

import RichTextBlock from "./RichTextBlock";
import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";
import FormSuccessResult from "./FormSuccessResult";
import MobileStoreButton from "./MobileStoreButton";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import { isValidEmail, isValidName } from "../utils/utils";

import ShiftPills from "../images/icons/shift-dashes.svg";

import "../styles/components/ProfessionalInfoForm.scss";

const DownloadAppsFooter = () => (
    <div className="text-center mt-3 d-md-none">
        <p className="px-3">Download the app to start claiming shifts!</p>
        <div className="d-flex justify-content-center mt-2">
            <MobileStoreButton className="mx-2" brand="apple" />
            <MobileStoreButton className="mx-2" brand="google" />
        </div>
    </div>
);

const ProfessionalInfoForm = ({ lead, submittedFormLink, successMessage }) => {
    const [role, setRole] = useState("");
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [organizationName, setOrganizationName] = useState("");
    const [privacyCheckbox, setPrivacyCheckbox] = useState(false);

    //form state
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateEmail = e => {
        setEmail(e.target.value);
        setValidity("email", isValidEmail(e.target.value));
    };

    const updateRole = e => {
        setRole(e.target.value);
    };

    const isValid = () => {
        const _errors = {
            role: !role,
            name: !isValidName(fullName),
            email: !isValidEmail(email),
            organizationName: !isValidName(organizationName),
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { professionalInfo: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            full_name: fullName,
            email,
            organization_name: organizationName,
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        try {
            const token = await reCAPTCHARef.current.executeAsync();
            submitForm(token);
        } catch (error) {
            console.error(error);
        }
    };

    if (submitted && !submissionError) {
        return (
            <FormSuccessResult
                submittedFormLink={submittedFormLink}
                successMessage={successMessage}
            >
                <DownloadAppsFooter />
            </FormSuccessResult>
        );
    }

    return (
        <Form
            className={classNames("professional-info-form", {
                "has-submit-attempt": hasSubmitAttempt,
            })}
            onSubmit={executeReCAPTCHA}
            key="professional-info-form"
        >
            <header className="form-header">
                {lead && <RichTextBlock content={lead} />}
            </header>
            <ShiftPills className="shift-pills" />

            <Form.Group className="mb-4">
                <Form.Label className="role-label" htmlFor="role">
                    What is your role? *
                </Form.Label>
                <Form.Check
                    value="nurse"
                    onChange={updateRole}
                    checked={role === "nurse"}
                    inline
                    label="Nurse"
                    name="role"
                    type="radio"
                    id="nurse-radio"
                ></Form.Check>
                <Form.Check
                    value="facility"
                    onChange={updateRole}
                    checked={role === "facility"}
                    inline
                    label="Facility"
                    name="role"
                    type="radio"
                    id="facility-radio"
                ></Form.Check>
            </Form.Group>

            <Form.Group className="mb-4" controlId="fullName">
                <Form.Control
                    type="text"
                    placeholder="Full Name *"
                    size="lg"
                    required
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    className={classNames({
                        invalid: !!errors["fullName"],
                    })}
                />
            </Form.Group>
            <Form.Group className="mb-4" controlId="email">
                <Form.Control
                    type="email"
                    placeholder="Email *"
                    size="lg"
                    required
                    value={email}
                    onChange={updateEmail}
                    className={classNames({
                        invalid: !!errors["email"],
                    })}
                />
            </Form.Group>

            <Form.Group className="mb-4" controlId="organizationName">
                <Form.Control
                    placeholder="Organization Name *"
                    size="lg"
                    required
                    value={organizationName}
                    onChange={e => setOrganizationName(e.target.value)}
                    className={classNames({
                        invalid: !!errors["organizationName"],
                    })}
                />
            </Form.Group>

            <Form.Group className="mb-4" controlId="privacy-check">
                <Form.Check
                    size="lg"
                    type="checkbox"
                    className="ms-1 privacy-check"
                >
                    <Form.Check.Input
                        type="checkbox"
                        required
                        checked={privacyCheckbox}
                        onChange={e => setPrivacyCheckbox(e.target.checked)}
                    />
                    <Form.Check.Label className="compliance ms-1">
                        I understand and agree to the terms of ShiftMed's{" "}
                        <Link to="/privacy/" target="privacy">
                            Privacy Policy.
                        </Link>
                    </Form.Check.Label>
                </Form.Check>
            </Form.Group>

            <Button
                variant="primary"
                type="submit"
                size="lg"
                disabled={submitting}
                className="px-5"
            >
                Submit
            </Button>

            {submissionError && (
                <Alert variant="danger">{submissionError}</Alert>
            )}

            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
        </Form>
    );
};

ProfessionalInfoForm.propTypes = {
    lead: PropTypes.shape({
        raw: PropTypes.string,
        references: PropTypes.array,
    }),
    successMessage: PropTypes.shape({
        raw: PropTypes.string,
        references: PropTypes.array,
    }),
    submittedFormLink: PropTypes.shape({
        label: PropTypes.string.isRequired,
        url: PropTypes.string,
        icon: PropTypes.shape({
            title: PropTypes.string,
            description: PropTypes.string,
            gatsbyImageData: PropTypes.object,
            file: PropTypes.shape({
                contentType: PropTypes.string.isRequired,
                url: PropTypes.string.isRequired,
            }),
        }),
        downloadAsset: PropTypes.shape({
            filename: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        }),
    }),
};

export default ProfessionalInfoForm;
